import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { RefreshCw, Settings, CheckCircle, XCircle, Clock, Loader2, AlertTriangle, Cloud, Activity, GitBranch } from "lucide-react";

export default function DevOpsSync() {
  const [settings, setSettings] = useState({ pat: "", projects_to_sync: [], sync_interval_hours: 8, org: "valorizasm" });
  const [devopsProjects, setDevOpsProjects] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingSprints, setSyncingSprints] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editPat, setEditPat] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const [sRes, lRes] = await Promise.all([
        axios.get(`${API}/devops/settings`).catch(() => ({ data: { pat: "", projects_to_sync: [], org: "valorizasm" } })),
        axios.get(`${API}/devops/sync-logs`),
      ]);
      setSettings(sRes.data);
      setEditPat(sRes.data.pat ? "••••••••••••••••" : "");
      setLogs(lRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    if (!window.confirm("⚠ Vas a lanzar la SINCRONIZACIÓN con Azure DevOps ahora (fuera del cron programado). ¿Continuar?")) return;
    setSyncing(true);
    try {
      await axios.post(`${API}/devops/sync`);
      setMsg("Sincronización de work items iniciada. Los datos se actualizarán en breve.");
      setTimeout(() => setMsg(""), 5000);
      setTimeout(load, 3000);
    } catch (e) { alert(e.response?.data?.error || "Error al sincronizar"); }
    finally { setSyncing(false); }
  };

  const handleSyncSprints = async () => {
    if (!window.confirm("⚠ Vas a lanzar la SINCRONIZACIÓN de Sprints con Azure DevOps ahora. ¿Continuar?")) return;
    setSyncingSprints(true);
    try {
      await axios.post(`${API}/devops/sync-sprints`);
      setMsg("Sincronización de sprints desde Azure DevOps iniciada.");
      setTimeout(() => setMsg(""), 5000);
      setTimeout(load, 3000);
    } catch (e) { alert(e.response?.data?.error || "Error al sincronizar sprints"); }
    finally { setSyncingSprints(false); }
  };

  const handleLoadProjects = async () => {
    setLoadingProjects(true);
    try {
      const { data } = await axios.get(`${API}/devops/projects`);
      if (data.error) { alert(data.error); }
      else { setDevOpsProjects(data.projects || []); }
    } catch (e) { alert("Error al cargar proyectos"); }
    finally { setLoadingProjects(false); }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...settings };
      if (editPat && !editPat.includes("•")) payload.pat = editPat;
      await axios.put(`${API}/devops/settings`, payload);
      setMsg("Configuración guardada");
      setTimeout(() => setMsg(""), 3000);
      setShowSettings(false);
      load();
    } catch (e) { alert("Error al guardar"); }
    finally { setSaving(false); }
  };

  const toggleProject = (name) => {
    setSettings(prev => ({
      ...prev,
      projects_to_sync: prev.projects_to_sync.includes(name)
        ? prev.projects_to_sync.filter(p => p !== name)
        : [...prev.projects_to_sync, name]
    }));
  };

  const lastLog = logs[0];
  const statusIcon = { completed: <CheckCircle size={16} className="text-green-500" />, running: <Loader2 size={16} className="text-yellow-500 animate-spin" />, error: <XCircle size={16} className="text-red-500" /> };
  const statusColor = { completed: "bg-green-100 text-green-700", running: "bg-yellow-100 text-yellow-700", error: "bg-red-100 text-red-700" };

  return (
    <div className="space-y-5 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Azure DevOps</h1>
          <p className="text-sm text-gray-500 mt-0.5">Sincronización con https://dev.azure.com/{settings.org || "valorizasm"}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSettings(!showSettings)} className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2" data-testid="devops-settings-btn">
            <Settings size={16} /> Configurar
          </button>
          <button onClick={handleSyncSprints} disabled={syncingSprints || !settings.pat_configured}
            className="border border-[#007934] text-[#007934] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#E8F5E9] flex items-center gap-2 disabled:opacity-60" data-testid="sync-sprints-btn">
            {syncingSprints ? <Loader2 size={16} className="animate-spin" /> : <GitBranch size={16} />}
            Sync Sprints
          </button>
          <button onClick={handleSync} disabled={syncing || !settings.pat_configured}
            className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] flex items-center gap-2 disabled:opacity-60" data-testid="sync-now-btn">
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            Sincronizar ahora
          </button>
        </div>
      </div>

      {msg && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"><CheckCircle size={16}/>{msg}</div>}

      {/* PAT Warning — R07 (auditoría externa, E14): antes se mostraba
          "PAT no configurado" en el primer render, antes de que la llamada
          a /devops/settings hubiera respondido siquiera una vez (conclusión
          falsa durante la carga). Ahora se espera a conocer el estado real. */}
      {!loading && !settings.pat_configured && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3" data-testid="pat-warning">
          <AlertTriangle size={18} className="text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-yellow-800">Azure DevOps PAT no configurado</p>
            <p className="text-xs text-yellow-600 mt-1">Configura el Personal Access Token para habilitar la sincronización automática con Azure DevOps.</p>
            <button onClick={() => setShowSettings(true)} className="mt-2 text-xs text-yellow-700 font-medium underline">Configurar ahora</button>
          </div>
        </div>
      )}
      {loading && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center gap-2 text-xs text-gray-500" data-testid="devops-config-checking">
          <Loader2 size={14} className="animate-spin" /> Comprobando configuración de Azure DevOps…
        </div>
      )}
      {!loading && settings.pat_configured && !settings.pat && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2 text-xs text-blue-700" data-testid="pat-env-notice">
          <CheckCircle size={14} /> PAT configurado vía variable de entorno del servidor (no editable desde aquí).
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Cloud size={16} className="text-[#007934]" />
            <span className="text-sm font-semibold text-gray-900">Último estado</span>
          </div>
          {loading ? (
            <p className="text-sm text-gray-400 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin"/> Cargando…</p>
          ) : lastLog ? (
            <div>
              <div className="flex items-center gap-2">{statusIcon[lastLog.status]}<span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[lastLog.status] || "bg-gray-100"}`}>{lastLog.status}</span></div>
              <p className="text-xs text-gray-400 mt-1">{new Date(lastLog.started_at).toLocaleString("es-ES")}</p>
            </div>
          ) : <p className="text-sm text-gray-400">Sin sincronizaciones</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={16} className="text-[#007934]" />
            <span className="text-sm font-semibold text-gray-900">Items sincronizados</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 font-['Outfit']">{loading ? "—" : (lastLog?.items_synced || 0)}</p>
          <p className="text-xs text-gray-400">en última sync</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-[#007934]" />
            <span className="text-sm font-semibold text-gray-900">Próxima sync</span>
          </div>
          <p className="text-sm font-medium text-gray-900">3 veces al día</p>
          <p className="text-xs text-gray-400">08:00 · 14:00 · 20:00</p>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="font-bold text-gray-900 font-['Outfit'] mb-4">Configuración Azure DevOps</h3>
          <form onSubmit={handleSaveSettings} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Organización</label>
              <input value={settings.org || "valorizasm"} readOnly
                className="w-full border border-gray-100 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Personal Access Token (PAT)</label>
              <input type="password" value={editPat} onChange={e => setEditPat(e.target.value)}
                placeholder="Ingresa el PAT de Azure DevOps..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="pat-input" />
              <p className="text-xs text-gray-400 mt-1">Obtén el PAT desde Azure DevOps &gt; User Settings &gt; Personal Access Tokens</p>
            </div>
            {/* Load projects button */}
            {editPat && !editPat.includes("•") && (
              <div>
                <button type="button" onClick={handleLoadProjects} disabled={loadingProjects}
                  className="border border-[#007934] text-[#007934] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#E8F5E9] flex items-center gap-2" data-testid="load-projects-btn">
                  {loadingProjects ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Cargar proyectos disponibles
                </button>
                {devopsProjects.length > 0 && (
                  <div className="mt-2 space-y-1 border border-gray-100 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {devopsProjects.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input type="checkbox"
                          checked={settings.projects_to_sync.includes(p.name)}
                          onChange={() => toggleProject(p.name)}
                          className="rounded border-gray-300 text-[#007934] focus:ring-[#007934]" />
                        <span className="text-sm">{p.name}</span>
                        <span className="text-xs text-gray-400">{p.state}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="bg-[#007934] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center gap-2" data-testid="save-devops-settings-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Guardar configuración
              </button>
              <button type="button" onClick={() => setShowSettings(false)} className="border border-gray-200 text-gray-700 px-5 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Sync Logs */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h3 className="font-bold text-gray-900 font-['Outfit'] mb-4">Historial de Sincronizaciones</h3>
        {logs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <RefreshCw size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin historial de sincronizaciones</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="sync-logs">
            {logs.map(log => (
              <div key={log.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-50 hover:bg-gray-50">
                <div className="flex-shrink-0">{statusIcon[log.status] || <Clock size={16} className="text-gray-400" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[log.status] || "bg-gray-100 text-gray-600"}`}>{log.status}</span>
                    {log.trigger === "scheduled" && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Automática</span>}
                    <span className="text-xs text-gray-500">{log.items_synced} items</span>
                    {log.projects_synced?.length > 0 && <span className="text-xs text-gray-400">{log.projects_synced.join(", ")}</span>}
                  </div>
                  {log.error && <p className="text-xs text-red-500 mt-0.5">{log.error}</p>}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{new Date(log.started_at).toLocaleString("es-ES")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
